/**
 * This device's own credential, remembered.
 *
 * [D-043](../../../docs/DECISIONS.md#d-043) has always said *the device holds
 * the credential, provisioned once by the owner* — and the app held it only in
 * memory, so every reopen asked a grown-up to type the device code again
 * ([D-131](../../../docs/DECISIONS.md#d-131)).
 *
 * `localStorage`, deliberately, where the child's session is in
 * `sessionStorage`: the session belongs to one child and ends with the tab;
 * the device credential belongs to the tablet and is exactly what makes it a
 * family tablet. D-043 accepts what this means — anyone holding the unlocked
 * device can open any profile on it — and bounds it: the credential is
 * revocable per device, and the device passcode is the real boundary.
 */

export interface RememberedDevice {
  readonly deviceId: string;
  readonly credential: string;
}

const KEY = 'capytalk.device';

export function readDevice(): RememberedDevice | null {
  try {
    const raw = globalThis.localStorage?.getItem(KEY) ?? null;
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as RememberedDevice).deviceId === 'string' &&
      typeof (parsed as RememberedDevice).credential === 'string'
    ) {
      return parsed as RememberedDevice;
    }
    return null;
  } catch {
    // Private browsing or storage refused: the grown-up types the code, as before.
    return null;
  }
}

export function rememberDevice(device: RememberedDevice): void {
  try {
    globalThis.localStorage?.setItem(KEY, JSON.stringify(device));
  } catch {
    // Not remembered, which costs a grown-up some typing next time and nothing else.
  }
}

export function forgetDevice(): void {
  try {
    globalThis.localStorage?.removeItem(KEY);
  } catch {
    // Nothing to forget.
  }
}
